import { defineStore, storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import { api } from '@/services/http.service';
import { ElNotification } from 'element-plus';
import { createCsvDataUrl } from '@/utils/downlod-file.util';
import { useTwoFactorStore } from '@/store/two-factor-dialog.store';
import { useTimestampStore } from '@/store/timestamp.store';
import { ApiErrorResponse } from '@/models/api/api-error.model';
import { FileDataResponse } from '@/models/app-internal/file-data-response.model';
import { useMgaReportListFilterStore } from '@/store/mga-reports/mga-report-list-filter.store';
import { ReportListCsvRequestModel } from '@/models/report/report.model';
import { downloadFile } from '@marfa/bo-utils';

interface MgaReportListCsvStore {
  isMgaReportListCsvLoading: boolean;
}

export const useMgaReportListCsvStore = defineStore({
  id: 'MgaReportListCsvStore',
  state: (): MgaReportListCsvStore => ({
    isMgaReportListCsvLoading: false,
  }),
  actions: {
    setMgaReportListCsvLoading(isLoading: boolean): void {
      this.isMgaReportListCsvLoading = isLoading;
    },
    getReportListCsv(): Promise<FileDataResponse | void> {
      this.setMgaReportListCsvLoading(true);
      ElNotification({
        title: 'Success',
        message: 'MGA report download has been started',
        type: 'success',
      });

      const { filterFormData } = storeToRefs(useMgaReportListFilterStore());
      const { twoFactorCode } = storeToRefs(useTwoFactorStore());
      const { isTimestampRequired } = storeToRefs(useTimestampStore());

      const requestPayload: ReportListCsvRequestModel = {
        timestamp: isTimestampRequired.value,
        filterList: filterFormData.value,
        code: twoFactorCode.value,
      };

      return api.report.getReportListCsvRequest.perform(requestPayload)
        .then((response) => {
          const { headers, ...data } = response;
          const csvString = Object.values(data).join('');
          const csvUrl = createCsvDataUrl(csvString);

          downloadFile(
            `mga-report-list-${dayjs().format('YYYY-MM-DD HH:mm:ss')}.csv`,
            csvUrl,
          );

          ElNotification({
            title: 'Success',
            message: 'MGA report has been download successfully',
            type: 'success',
          });

          return response;
        })
        .catch((error: ApiErrorResponse) => {
          ElNotification({
            title: 'Error',
            message: error.response.data.message,
            type: 'error',
          });
        })
        .finally(() => {
          this.setMgaReportListCsvLoading(false);
        });
    },
  },
});
